import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { UserViewStore } from "../../store";
import { UserView } from "../view/UserView";
import { UserUpdateStore } from "../../store";
import { UserUpdate } from "../update/UserUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type UserModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const UserModalContent: React.FC<UserModalContentProps> = observer(
  ({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new UserUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new UserViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <UserView userViewStore={storeInfo.store as UserViewStore} />
        )}
        {storeInfo.type === "update" && (
          <UserUpdate
            userUpdateStore={storeInfo.store as UserUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  }
);
